import { FC, FocusEventHandler, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { SystemStyleObject, Theme } from '@mui/system';

import { ReactComponent as ChevronIcon } from '@/assets/icons/chevron-up-icon.svg';
import { ReactComponent as FolderIcon } from '@/assets/icons/folder-icon.svg';
import AppButton from '@/shared/ui/AppButton';

interface Props {
  files: number;
  /** Left out where the folder has no link count of its own, as a plan line's has not. */
  links?: number;
  /** With no menu the pill loses its chevron and does nothing on click. */
  hasMenu?: boolean;
  open?: boolean;
  loading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onMouseEnter?: MouseEventHandler<HTMLButtonElement>;
  onFocus?: FocusEventHandler<HTMLButtonElement>;
}

const pillSx: SystemStyleObject<Theme> = {
  gap: '8px',
  p: '8px',
  borderRadius: '12px',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.3,
  color: 'grey.800',
  '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': { color: 'accent.main' },
  '&:hover svg, &.Mui-disabled svg': { color: 'inherit' },
};

const closedSx: SystemStyleObject<Theme> = {
  '& .MuiButton-endIcon svg': { transform: 'rotate(180deg)' },
};

const countsSx: SystemStyleObject<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const bulletSx: SystemStyleObject<Theme> = { fontSize: '11px' };

/** The Artwork cell's trigger: "N files • M links" behind a folder, with the chevron that opens the folder's menu. */
const DriveFolderPill: FC<Props> = ({
  files,
  links,
  hasMenu = true,
  open = false,
  loading = false,
  onClick,
  onMouseEnter,
  onFocus,
}) => {
  const { t } = useTranslation();

  return (
    <AppButton
      variant="secondary"
      size="small"
      startIcon={<FolderIcon width={19} height={19} />}
      endIcon={hasMenu ? <ChevronIcon width={17} height={17} /> : undefined}
      loading={loading}
      disabled={!hasMenu}
      aria-haspopup={hasMenu ? 'true' : undefined}
      aria-expanded={hasMenu ? open : undefined}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      data-testid="drive-folder-counts"
      sx={[pillSx, ...(open ? [] : [closedSx])]}
    >
      {links === undefined ? (
        t('artwork_drive.file_counts', { files })
      ) : (
        <Box component="span" sx={countsSx}>
          <span>{t('artwork_drive.file_counts', { files })}</span>
          <Box component="span" sx={bulletSx}>
            •
          </Box>
          <span>{t('artwork_drive.link_counts', { links })}</span>
        </Box>
      )}
    </AppButton>
  );
};

export default DriveFolderPill;
